import React, { useEffect, useState } from "react";
import { useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { useNavigate } from "react-router-dom";
import Skeleton from '@mui/material/Skeleton';
import { toast } from 'react-toastify';
import axios from 'axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Alert from '@mui/material/Alert';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';

import Product from "../../models/product";
import Price from "../../models/price";
import ComboComponentManager from "./ComboComponentManager";
import { getProducts } from "../../collections/products";

const EditProduct = (props) => {
  const navigate = useNavigate();
  const [productData, setProductData] = useState({
    name: '',
    description: '',
    image: null,
    expire_days: '',
    convert_rate: '',
    price: null,
    created_at: '',
    updated_at: ''
  });
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const [error, setError] = useState('');
  const [newImage, setNewImage] = useState(null);
  const [priceList, setPriceList] = useState([]);
  const [isCombo, setIsCombo] = useState(false);
  const [originalIsCombo, setOriginalIsCombo] = useState(false);
  const [components, setComponents] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [conversionDialogOpen, setConversionDialogOpen] = useState(false);
  const [conversionInfo, setConversionInfo] = useState(null);
  const [inventoryOption, setInventoryOption] = useState('clear');
  const [productStatus, setProductStatus] = useState('ACTIVE');
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusError, setStatusError] = useState(null);

  useEffect(() => {
    props.setPageTitle("Chỉnh sửa sản phẩm");
    const fetchProduct = async () => {
      setLoading(true);
      try {
        // Load all products for component selector
        const productsResponse = await getProducts();
        if (productsResponse && productsResponse.data) {
          setAllProducts(productsResponse.data);
        }

        const product = new Product();
        const data = await product.getProductById(id);
        setProductData(data);
        setIsCombo(data.is_combo || false);
        setOriginalIsCombo(data.is_combo || false);
        setProductStatus(data.status || 'ACTIVE');

        // Load components if it's a combo
        if (data.is_combo) {
          const componentsData = await product.getComboComponents(id);
          setComponents(componentsData);
        }

        const price = new Price();
        const priceData = await price.getPriceById(id);

        if (priceData) {
          setPriceList(priceData);
          setProductData((prevData) => ({
            ...prevData,
            ...priceData
          }));
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, props]);
  console.log(productData);

  const uploadImage = async (imageFile) => {
    let data = new FormData();
    data.append('file', imageFile);
    let config = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: 'https://api.maysfarm.vn/api/v1/images',
      headers: {
        'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MSwiZW1haWwiOiJuZ3V5ZW50aGFpMjgxMUBnbWFpbC5jb20iLCJyb2xlIjoiQURNSU4iLCJleHAiOjEwMDAwMDAwMDAwMDE3MjQ4NDY5NTh9.KtSXIt4Du_lhqfZNwyGCETlCW9BaIXUujjSW60SfaqY',
      },
      data: data
    };
    try {
      const response = await axios.request(config);
      return response.data.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const removeImage = () => {
    setNewImage(null);
    setProductData((prevData) => ({
      ...prevData,
      image: null
    }));
  };

  const handleComboCheckboxChange = async (event) => {
    const newIsCombo = event.target.checked;

    // Check if conversion is attempted
    if (newIsCombo !== originalIsCombo) {
      // Fetch conversion info
      const product = new Product();
      const infoResult = await product.getConversionInfo(id);

      if (infoResult.success) {
        setConversionInfo(infoResult.data);
        setIsCombo(newIsCombo);
        setConversionDialogOpen(true);
      } else {
        toast.error(infoResult.message);
      }
    }
  };

  const handleConversionConfirm = async () => {
    const product = new Product();
    const convertToCombo = isCombo;
    const clearInventory = inventoryOption === 'clear';

    const result = await product.convertProduct(id, convertToCombo, clearInventory);

    if (result.success) {
      toast.success(result.message, {
        position: "top-center",
        autoClose: 3000,
      });
      setConversionDialogOpen(false);
      setOriginalIsCombo(isCombo);

      // Reload product data
      const data = await product.getProductById(id);
      setProductData(data);

      // Reload components if converted to combo
      if (convertToCombo) {
        const componentsData = await product.getComboComponents(id);
        setComponents(componentsData);
      } else {
        setComponents([]);
      }
    } else {
      toast.error(result.message || result.details?.error, {
        position: "top-center",
        autoClose: 5000,
      });
    }
  };

  const handleConversionCancel = () => {
    setConversionDialogOpen(false);
    setIsCombo(originalIsCombo);
  };

  const handleStatusChange = (event) => {
    const newStatus = event.target.checked ? 'ACTIVE' : 'INACTIVE';
    if (newStatus === 'INACTIVE') {
      setStatusDialogOpen(true);
    } else {
      setProductStatus(newStatus);
    }
  };

  const handleStatusConfirm = () => {
    setProductStatus('INACTIVE');
    setStatusDialogOpen(false);
  };

  const handleStatusCancel = () => {
    setStatusDialogOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    let validationErrors = {};
    if (!data.get('new_name')) validationErrors.name = 'Tên sản phẩm không được để trống!';
    if (!data.get('new_expire_days')) validationErrors.expire_days = 'Hạn sử dụng không được để trống!';
    if (priceList.length === 0 && !data.get('new_price')) validationErrors.price = 'Đơn giá không được để trống!';
    if (!data.get('new_convert_rate')) validationErrors.convert_rate = 'Tỉ lệ chuyển đổi không được để trống!';

    setError(validationErrors);
    console.log(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      const imageFile = data.get('new_image');
      let imageName = productData.image;
      if (imageFile) {
        imageName = await uploadImage(imageFile);
      }
      const product = new Product({
        name: data.get('new_name'),
        description: data.get('new_description'),
        image: imageName,
        expire_days: data.get('new_expire_days'),
        convert_rate: data.get('new_convert_rate'),
        status: productStatus,
      })
      const currentDateTime = new Date().toISOString().split('.')[0];
      const price = new Price({
        product_id: productData.id,
        price: data.get('new_price'),
        actived_at: currentDateTime,
      })
      try {
        if (!data.get('new_price')) {
          const response = await product.updateProduct(id);
          if (response.success) {
            toast.success(response.message, {
              position: "top-center",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
            navigate("/products");
          } else {
            // Handle combo validation error
            setStatusError(response.message);
            setProductStatus(productData.status || 'ACTIVE');
            toast.error(response.message, {
              position: "top-center",
              autoClose: 5000,
            });
          }
        }
        else {
          const response = await product.updateProduct(id);
          if (response.success) {
            const responsePrice = await price.addPrice(id);
            if (responsePrice.success) {
              toast.success(response.message, {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
              });
              navigate("/products");
            }
          } else {
            // Handle combo validation error
            setStatusError(response.message);
            setProductStatus(productData.status || 'ACTIVE');
            toast.error(response.message, {
              position: "top-center",
              autoClose: 5000,
            });
          }
        }
      }
      catch (error) {
        console.log(error);
        toast.error("Có lỗi xảy ra khi cập nhật sản phẩm", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    }

  }

  if (loading) {
    return (
      <Skeleton
        animation="wave"
        variant="rectangular"
        height={250}
        sx={{ m: 3 }}
      />
    )
  }

  return (
    <Container sx={{ mt: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box component="form" onSubmit={handleSubmit} flex={1}>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isCombo}
                    onChange={handleComboCheckboxChange}
                    name="is_combo"
                  />
                }
                label="Sản phẩm combo?"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                {isCombo !== originalIsCombo && '(Chưa lưu - cần xác nhận chuyển đổi)'}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={productStatus === 'ACTIVE'}
                    onChange={handleStatusChange}
                    name="status"
                    color="success"
                  />
                }
                label={productStatus === 'ACTIVE' ? "Đang hoạt động" : "Ngừng hoạt động"}
              />
              {statusError && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setStatusError(null)}>
                  {statusError}
                </Alert>
              )}
            </Box>
            <TextField
              id="new_name"
              name="new_name"
              label="Tên sản phẩm"
              fullWidth
              margin="normal"
              variant="outlined"
              value={productData.name}
              onChange={(event) => setProductData({ ...productData, name: event.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              error={!!error.name}
              helperText={error.name}
            />
            {productData.image && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Hình ảnh"
                  variant="outlined"
                  value={newImage ? newImage.split('/').pop() : productData.image.split('/').pop()}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Button
                        variant="contained"
                        color="error"
                        onClick={removeImage}
                        sx={{ ml: 1 }}
                      >
                        X
                      </Button>
                    ),
                  }}
                />
                <Box sx={{ mt: 1 }}>
                  <img src={newImage || productData.image} alt="Current product" style={{ width: '100%', height: 'auto' }} />
                </Box>
              </Box>
            )}
            {!productData.image && (
              <TextField
                margin="normal"
                fullWidth
                name="new_image"
                label="Hình ảnh"
                id="new_image"
                type="file"
                InputLabelProps={{ shrink: true }}
                error={!!error.image}
                helperText={error.image}
              />
            )}
            <TextField
              id="new_description"
              name="new_description"
              label="Mô tả"
              fullWidth
              margin="normal"
              variant="outlined"
              value={productData.description}
              onChange={(event) => setProductData({ ...productData, description: event.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              error={!!error.description}
              helperText={error.description}
            />
            <TextField
              id="new_price"
              name="new_price"
              label="Thêm đơn giá mới"
              fullWidth
              margin="normal"
              variant="outlined"
              value={productData.price ? productData.price.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' }) : productData.price}
              onChange={(event) => setProductData({ ...productData, price: event.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              error={!!error.price}
              helperText={error.price}
            />
            <TextField
              id="new_expire_days"
              name="new_expire_days"
              label="Hạn sử dụng"
              fullWidth
              margin="normal"
              variant="outlined"
              value={productData.expire_days}
              onChange={(event) => setProductData({ ...productData, expire_days: event.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              error={!!error.expire_days}
              helperText={error.expire_days}
            />
            <TextField
              id="new_convert_rate"
              name="new_convert_rate"
              label="Tỉ lệ chuyển đổi"
              fullWidth
              margin="normal"
              variant="outlined"
              value={productData.convert_rate}
              onChange={(event) => setProductData({ ...productData, convert_rate: event.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              error={!!error.convert_rate}
              helperText={error.convert_rate}
            />
            {isCombo && (
              <ComboComponentManager
                productId={id}
                availableProducts={allProducts}
                onComponentsChange={setComponents}
              />
            )}
            <TextField
              label="Ngày tạo"
              fullWidth
              margin="normal"
              variant="outlined"
              value={new Date(productData.created_at).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              label="Chỉnh sửa giá lần cuối"
              fullWidth
              margin="normal"
              variant="outlined"
              value={new Date(productData.updated_at).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Button
              variant="contained"
              type="submit"
            >
              Lưu
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <TableContainer component={Paper} flex={1}>
            <Table aria-label="price history table">
              <TableHead>
                <TableRow>
                  <TableCell>Giá</TableCell>
                  <TableCell>Thời gian kích hoạt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {priceList
                  .slice()
                  .sort((a, b) => new Date(b.actived_at) - new Date(a.actived_at))
                  .map((price, index) => (
                    <TableRow key={index}>
                      <TableCell>{price.price.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}</TableCell>
                      <TableCell>{new Date(price.actived_at).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Conversion Dialog */}
      <Dialog
        open={conversionDialogOpen}
        onClose={handleConversionCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            {isCombo ? 'Chuyển sang Sản phẩm Combo?' : 'Chuyển sang Sản phẩm Thường?'}
          </Box>
        </DialogTitle>
        <DialogContent>
          {conversionInfo && (
            <>
              {isCombo ? (
                /* Converting to Combo */
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>Sản phẩm Combo không giữ tồn kho riêng.</strong>
                    </Typography>
                    <Typography variant="body2">
                      Tồn kho được tính từ các thành phần của combo.
                    </Typography>
                  </Alert>

                  {conversionInfo.current_inventory !== 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          <strong>Tồn kho hiện tại: {conversionInfo.current_inventory}kg</strong>
                        </Typography>
                        <Typography variant="body2">
                          Bạn muốn xử lý tồn kho này như thế nào?
                        </Typography>
                      </Alert>

                      <RadioGroup
                        value={inventoryOption}
                        onChange={(e) => setInventoryOption(e.target.value)}
                      >
                        <FormControlLabel
                          value="clear"
                          control={<Radio />}
                          label="Xóa tồn kho (tạo giao dịch điều chỉnh về 0)"
                        />
                        <FormControlLabel
                          value="keep"
                          control={<Radio />}
                          label="Giữ tồn kho (sẽ bị bỏ qua, không sử dụng)"
                        />
                      </RadioGroup>
                    </Box>
                  )}

                  <Alert severity="info" icon={<InfoIcon />}>
                    <Typography variant="body2">
                      <strong>Lưu ý:</strong> Sau khi chuyển đổi, bạn cần thêm các thành phần cho combo.
                      Lịch sử đơn hàng cũ KHÔNG bị ảnh hưởng.
                    </Typography>
                  </Alert>
                </>
              ) : (
                /* Converting to Regular */
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>Thao tác này sẽ xóa tất cả thành phần:</strong>
                    </Typography>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {conversionInfo.components && conversionInfo.components.map((comp, idx) => (
                        <li key={idx}>
                          {comp.quantity}× {comp.component_product_name}
                        </li>
                      ))}
                    </ul>
                  </Alert>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>Sau khi chuyển đổi:</strong>
                    </Typography>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>Tồn kho bắt đầu từ 0kg (cần nhập hàng mới)</li>
                      <li>Có thể liên kết với nhà cung cấp</li>
                      <li>Có thể thêm vào đơn đặt hàng</li>
                    </ul>
                  </Alert>

                  {conversionInfo.has_recent_orders && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <WarningIcon sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        <strong>Cảnh báo:</strong> Sản phẩm này có {conversionInfo.recent_orders_count} đơn hàng trong 30 ngày qua.
                        Chuyển đổi ngay có thể gây nhầm lẫn khi xem lại đơn hàng gần đây.
                      </Typography>
                    </Alert>
                  )}

                  {conversionInfo.used_as_component_in && conversionInfo.used_as_component_in.length > 0 && (
                    <Alert severity="error">
                      <Typography variant="body2">
                        <strong>Không thể chuyển đổi:</strong> Sản phẩm này đang được sử dụng làm thành phần trong combo khác.
                        Vui lòng xóa khỏi các combo đó trước.
                      </Typography>
                    </Alert>
                  )}

                  <Alert severity="info" icon={<InfoIcon />}>
                    <Typography variant="body2">
                      <strong>Lưu ý:</strong> Lịch sử đơn hàng combo cũ KHÔNG bị ảnh hưởng.
                    </Typography>
                  </Alert>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConversionCancel}>
            Hủy
          </Button>
          <Button
            onClick={handleConversionConfirm}
            variant="contained"
            color={isCombo ? "secondary" : "primary"}
            disabled={conversionInfo && !conversionInfo.can_convert}
          >
            {isCombo ? 'Chuyển sang Combo' : 'Chuyển sang Thường'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Confirmation Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={handleStatusCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Xác nhận ngừng hoạt động sản phẩm
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Bạn có chắc chắn muốn ngừng hoạt động sản phẩm này?</strong>
            </Typography>
            <Typography variant="body2">
              Sản phẩm ngừng hoạt động sẽ:
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Không hiển thị khi tạo/sửa đơn hàng</li>
              <li>Không thể thêm vào combo</li>
              <li>Hiển thị mờ trong danh sách sản phẩm</li>
            </ul>
          </Alert>
          <Alert severity="info" icon={<InfoIcon />}>
            <Typography variant="body2">
              <strong>Lưu ý:</strong> Bạn có thể kích hoạt lại sản phẩm bất cứ lúc nào.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStatusCancel}>
            Hủy
          </Button>
          <Button
            onClick={handleStatusConfirm}
            variant="contained"
            color="warning"
          >
            Xác nhận ngừng hoạt động
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default EditProduct;
